import { requireRoleDashboardContext } from "../RoleDashboardContext";
import { Group } from "./Group";
import { For, Show, createEffect, createMemo } from "solid-js";
import { studentEntityHistory, setStudentEntityHistory } from 'sis-app';
import { StudentEntityRecord, ViewDefInfo, roleDashboard as api, studentSelect } from "sis-app/api";
import { Image, Flexbox, ImageButton, requireFocusContext, fetchFocusKey, processResponseActions, LayoutTable } from "st-app";
import styles from '../sis-role-dashboard.module.css'
import { date, focus } from "common";

const STUDENT_VIEW_GU = '3D8BCF52-C4A1-485B-A2E6-C385FE1B6A9E';

export function UserRecentHistory() {
    const ctx = requireRoleDashboardContext();

    createEffect(() => {
        ctx.refreshTrigger();
        studentSelect.GetUserHistory().then(setStudentEntityHistory);
    });

    return <Show when={studentEntityHistory().students.length > 0}>
        <Group title="History" icon="fas fa-folder-open" color="--header-amber" style="min-width: 500px; max-width: 500px;">
            <div style="overflow-y: auto; min-width: 100%">
                <LayoutTable class={styles.userHistory} style="width:100%; overflow-y: auto" >
                    <For each={studentEntityHistory().students}>{stu =>
                        <StudentCard student={stu} />
                    }</For>
                </LayoutTable>
            </div>
        </Group>
    </Show>
}

function StudentCard(props: { student: StudentEntityRecord }) {
    const ctx = requireRoleDashboardContext();

    const views = createMemo(() => {
        return studentEntityHistory().views
            .reduce((res: Record<string, ViewDefInfo>, v: ViewDefInfo) => {
                res[v.viewGU ?? v.reportNumber ?? ''] = v;
                return res;
            }, {} as Record<string, ViewDefInfo>);
    });

    const lastAccessDate = createMemo(() => {
        const dt = new Date(props.student.lastAccessInfo.timestamp || '');
        return date.relativeFormat(dt);
    });

    const lastAccessFocus = createMemo(() => {
        return props.student.lastAccessInfo.focusOrganizationName;
    });

    function openStudentView(ev: Event) {
        api.OpenDetachedView(
            STUDENT_VIEW_GU,
            props.student.studentGU ?? '',
            props.student.lastAccessInfo?.focusOrganizationGU ?? '',
            props.student.lastAccessInfo?.focusYearGU ?? ''
        ).then(processResponseActions);

        ev.stopPropagation();
        ev.preventDefault();
    }

    return <LayoutTable.Row padded bordered align="top" class={styles.studentCard} onClick={openStudentView} >
        <LayoutTable.Column>
            <Image useDefaultPhoto src={props.student.photo} alt="Student Photo" width="32px" style="max-height: 48px" />
        </LayoutTable.Column>
        <LayoutTable.Column auto>
            <Flexbox dir="vertical" style={{ 'font-size': '0.9em' }} >
                <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                    <a href="#" onClick={openStudentView} >
                        {props.student.formattedName}
                    </a>
                </Flexbox.Item>
                <Flexbox.Item>Sis Number: <strong>{props.student.sisNumber}</strong></Flexbox.Item>
            </Flexbox>
            <Flexbox dir="horizontal" class={styles.historyDetails}>
                <Flexbox.Item auto>{lastAccessFocus()}</Flexbox.Item>
                <Flexbox.Item>{lastAccessDate()}</Flexbox.Item>
            </Flexbox>
        </LayoutTable.Column>
        <LayoutTable.Column>
            <Flexbox dir="horizontal" middle class={styles.viewButtons} >
                <For each={props.student.recentViews}>{viewGU =>
                    <ViewButton view={views()[viewGU]} student={props.student} />
                }</For>
            </Flexbox>
        </LayoutTable.Column>
    </LayoutTable.Row>
}


function ViewButton(props: { student: StudentEntityRecord, view: ViewDefInfo }) {
    const view = props.view;
    const stu = props.student;

    const classes = createMemo(() => {
        const res = [styles.viewButton];
        if (stu.recentViews.includes(view.viewGU || view.reportNumber || '')) {
            res.push(styles.recent);
        }

        return res.join(' ');
    });

    function openView(ev: MouseEvent) {
        // Prevent event from bubbling to parent row
        ev.stopPropagation();
        ev.preventDefault();

        if (view.isReport) {
            throw new Error('Only views can be launched to a specific entity.');
        }
        else {
            api.OpenDetachedView(
                view.viewGU ?? '',
                stu.studentGU ?? '',
                stu.lastAccessInfo.focusOrganizationGU ?? '',
                stu.lastAccessInfo.focusYearGU ?? ''
            ).then(processResponseActions);
        }
    }

    return <Flexbox.Item class={classes()} >
        <ImageButton
            tooltip={view.viewName || view.reportName}
            defaultSrc={view.isReport ? 'report' : 'view'}
            label={view.viewName || view.reportName || ''}
            src={view.viewIcon}
            width={24}
            onClick={openView} />
    </Flexbox.Item>
}
